/// <reference path="../../types/index.d.ts" />

import { Action } from '../models/Action';
import { Timer } from '../models/Timer';
import { Variable } from '../models/Variable';
import { RagsObject } from '../models/Object';
import { Character } from '../models/Character';
import { Room } from '../models/Room';
import { CustomProperty } from '../models/CustomProperty';

declare let TheGame: any;

/**
 * Finder utility - provides fast lookup for game entities
 */

interface NameMap {
    [key: string]: any;
}

interface UidMap {
    [key: string]: any;
}

interface GroupMap {
    [key: string]: any[];
}

export const Finder = {
    action: function(actions: Action[], name: string): Action | undefined {
        const lowercaseActionName = name.trim().toLowerCase();
        if ((actions as any).NameMap !== undefined) {
            return (actions as any).NameMap[lowercaseActionName];
        }
        console.debug("No NameMap for actions", actions, name);
        return actions.find((action) => {
            return action.name.trim().toLowerCase() === lowercaseActionName;
        });
    },

    timer: function(timerName: string): Timer | undefined {
        if (!TheGame || !TheGame.Timers) return undefined;
        const lowercaseTimerName = timerName.trim().toLowerCase();
        if ((TheGame.Timers as any).NameMap !== undefined) {
            return (TheGame.Timers as any).NameMap[lowercaseTimerName];
        }
        console.debug("No NameMap for timers", timerName);
        return TheGame.Timers.find((timer: any) => {
            return timer.Name.trim().toLowerCase() === lowercaseTimerName;
        });
    },

    variable: function(variableName: string): Variable | undefined {
        if (!TheGame || !TheGame.Variables) return undefined;
        if (variableName.indexOf("(") > -1) {
            variableName = variableName.substring(0, variableName.indexOf("("));
        }
        variableName = variableName.trim();

        const lowercaseVariableName = variableName.toLowerCase();
        if ((TheGame.Variables as any).NameMap !== undefined) {
            return (TheGame.Variables as any).NameMap[lowercaseVariableName];
        }
        console.debug("No NameMap for variables", variableName);
        return TheGame.Variables.find((variable: any) => {
            return variable.varname.trim().toLowerCase() === lowercaseVariableName;
        });
    },

    object: function(uidOrName: string | null | undefined): RagsObject | undefined {
        if (!TheGame || !TheGame.Objects) return undefined;
        if (!uidOrName) {
            return undefined;
        }
        uidOrName = uidOrName.trim();

        if ((TheGame.Objects as any).UidMap !== undefined) {
            const obj = (TheGame.Objects as any).UidMap[uidOrName];
            if (obj !== undefined) return obj;
        } else {
            console.debug("No UidMap for objects", uidOrName);
            for (let i = 0; i < TheGame.Objects.length; i++) {
                if (TheGame.Objects[i].UniqueIdentifier === uidOrName) {
                    return TheGame.Objects[i];
                }
            }
        }

        const lowercaseObjectName = uidOrName.toLowerCase();
        if ((TheGame.Objects as any).NameMap !== undefined) {
            return (TheGame.Objects as any).NameMap[lowercaseObjectName];
        }
        console.debug("No NameMap for objects", uidOrName);
        for (let j = 0; j < TheGame.Objects.length; j++) {
            if (TheGame.Objects[j].name && TheGame.Objects[j].name.trim().toLowerCase() === lowercaseObjectName) {
                return TheGame.Objects[j];
            }
        }
        return undefined;
    },

    character: function(characterName: string | null): Character | undefined {
        if (!TheGame || !TheGame.Characters) return undefined;
        if (characterName == null) {
            return undefined;
        }

        const lowercaseCharacterName = characterName.trim().toLowerCase();
        if ((TheGame.Characters as any).NameMap !== undefined) {
            return (TheGame.Characters as any).NameMap[lowercaseCharacterName];
        }
        console.debug("No NameMap for characters", characterName);
        for (let i = 0; i < TheGame.Characters.length; i++) {
            if (TheGame.Characters[i].Charname.trim().toLowerCase() == lowercaseCharacterName) {
                return TheGame.Characters[i];
            }
        }
        return undefined;
    },

    room: function(roomName: string | null): Room | undefined {
        if (!TheGame || !TheGame.Rooms) return undefined;
        if (roomName == null) {
            return undefined;
        }

        roomName = roomName.trim();
        if ((TheGame.Rooms as any).UidMap !== undefined) {
            const room = (TheGame.Rooms as any).UidMap[roomName];
            if (room !== undefined) return room;
        } else {
            console.debug("No UidMap for rooms", roomName);
            for (let i = 0; i < TheGame.Rooms.length; i++) {
                if (TheGame.Rooms[i].UniqueID == roomName) {
                    return TheGame.Rooms[i];
                }
            }
        }

        const containsDash = roomName.indexOf('-') != -1;
        const lowercaseRoomName = roomName.toLowerCase();
        if ((TheGame.Rooms as any).NameMap !== undefined) {
            return (TheGame.Rooms as any).NameMap[lowercaseRoomName];
        }
        console.debug("No NameMap for rooms", roomName);
        //check by name if we get here
        for (let j = 0; j < TheGame.Rooms.length; j++) {
            const room = TheGame.Rooms[j];
            if (room.Name.trim().toLowerCase() == lowercaseRoomName) {
                return TheGame.Rooms[j];
            }

            // Though it usually produces a UniqueID, sometimes
            // when you manually edit a field in the Rags designer
            // (instead of selecting from a dropdown) the value
            // for a room (in e.x. CT_MOVEPLAYER) will be the
            // room name or `%{name}-%{sdesc}`. So we need to check for that.
            if (containsDash && room.SDesc) {
                const joinedName = [room.Name, room.SDesc].join('-');
                if (joinedName.trim().toLowerCase() == lowercaseRoomName) {
                    return room;
                }
            }
        }
        return undefined;
    },

    customProp: function (obj: any, propertyName: string): CustomProperty | undefined {
        if ((obj.CustomProperties as any).NameMap !== undefined) {
            return (obj.CustomProperties as any).NameMap[propertyName];
        }
        console.debug("No NameMap for custom properties", obj, propertyName);
        for (let i = 0; i < obj.CustomProperties.length; i++) {
            const curprop = obj.CustomProperties[i];
            if (curprop.Name == propertyName) {
                return curprop;
            }
        }
        return undefined;
    },

    objectGroup: function(groupName: string): RagsObject[] {
        if (!TheGame || !TheGame.Objects) return [];
        const key = (groupName || '').trim().toLowerCase();
        if (key === '') return [];
        const group = (TheGame.Objects as any).GroupMap?.[key];
        return group === undefined ? [] : group;
    },

    roomGroup: function(groupName: string): Room[] {
        if (!TheGame || !TheGame.Rooms) return [];
        const key = (groupName || '').trim().toLowerCase();
        if (key === '') return [];
        const group = (TheGame.Rooms as any).GroupMap?.[key];
        return group === undefined ? [] : group;
    },

    characterGroup: function(groupName: string): Character[] {
        if (!TheGame || !TheGame.Characters) return [];
        const key = (groupName || '').trim().toLowerCase();
        if (key === '') return [];
        const group = (TheGame.Characters as any).GroupMap?.[key];
        return group === undefined ? [] : group;
    },

    variableGroup: function(groupName: string): Variable[] {
        if (!TheGame || !TheGame.Variables) return [];
        const key = (groupName || '').trim().toLowerCase();
        if (key === '') return [];
        const group = (TheGame.Variables as any).GroupMap?.[key];
        return group === undefined ? [] : group;
    },

    timerGroup: function(groupName: string): Timer[] {
        if (!TheGame || !TheGame.Timers) return [];
        const key = (groupName || '').trim().toLowerCase();
        if (key === '') return [];
        const group = (TheGame.Timers as any).GroupMap?.[key];
        return group === undefined ? [] : group;
    },

    imageGroup: function(groupName: string): any[] {
        if (!TheGame || !TheGame.Images) return [];
        const key = (groupName || '').trim().toLowerCase();
        if (key === '') return [];
        const group = (TheGame.Images as any).GroupMap?.[key];
        return group === undefined ? [] : group;
    },

    actionOwner: function(action: Action): any {
        if (action._owner === undefined) {
            console.warn('Found action with no owner', action);
        }
        return action._owner;
    },

    /** Add NameMap, UidMap and GroupMap to game arrays to speed up the searches. */
    addMaps: function(gameData: any): void {
        function buildMap(list: any[], keyFunc: (item: any) => string | undefined, map?: NameMap): NameMap {
            if (map === undefined) map = {};

            list.forEach((item) => {
                const key = keyFunc(item);
                if (key === undefined) return;
                if (key in map!) {
                    console.warn(key + " found multiple times", item, list);
                } else {
                    map![key] = item;
                }
            });

            return map!;
        }

        function buildNameMap(list: any[], propName: string, map?: NameMap): NameMap {
            return buildMap(list, (item) => {
                if (propName in item /*&& item[propName]*/) {
                    return item[propName].trim().toLowerCase();
                }
                return undefined;
            }, map);
        }

        function buildUidMap(list: any[], propName: string, map?: UidMap): UidMap {
            return buildMap(list, (item) => {
                return item[propName];
            }, map) as UidMap;
        }

        function normalizeGroupKey(value: any): string {
            return String(value || '').trim().toLowerCase();
        }

        function addToGroupMap(map: GroupMap, groupName: any, item: any): void {
            const key = normalizeGroupKey(groupName);
            if (key === '') return;
            const list = map[key];
            if (list === undefined) {
                map[key] = [item];
            } else {
                list.push(item);
            }
        }

        function buildGroupMap(list: any[], propName: string): GroupMap {
            const map: GroupMap = {};

            list.forEach((item) => {
                const name = item[propName];
                if (name === undefined || name === '') return;

                addToGroupMap(map, name, item);
            });

            return map;
        }

        function buildIdLookup(list: any[], idResolver: (item: any) => string | string[] | undefined | null): Map<string, any> {
            const map = new Map<string, any>();
            list.forEach((item) => {
                const ids = idResolver(item);
                if (!ids) return;
                const entries = Array.isArray(ids) ? ids : [ids];
                entries.forEach((entry) => {
                    const key = normalizeGroupKey(entry);
                    if (key !== '' && !map.has(key)) {
                        map.set(key, item);
                    }
                });
            });
            return map;
        }

        function applyGroupAssignments(
            list: any[],
            assignments: Record<string, string> | undefined,
            idResolver: (item: any) => string | string[] | undefined | null,
            groupProp: string | null,
            map: GroupMap
        ): void {
            if (!assignments) return;
            const lookup = buildIdLookup(list, idResolver);
            Object.keys(assignments).forEach((rawKey) => {
                const key = normalizeGroupKey(rawKey);
                if (key === '') return;
                const item = lookup.get(key);
                if (!item) return;
                const groupName = assignments[rawKey];
                if (groupProp) {
                    item[groupProp] = groupName;
                }
                addToGroupMap(map, groupName, item);
            });
        }

        // NameMap: Characters, Characters[].Actions, Characters[].CustomProperties,
        //          Objects, Objects[].Actions, Objects[].CustomProperties,
        //          Player.Actions, Player.CustomProperties,
        //          Rooms, Rooms[].Actions, Rooms[].CustomProperties,
        //          Timers, Timers[].Actions, Timers[].CustomProperties,
        //          Variables
        const nameMapTargets: [any[], string][] = [
            // [list, propName]
            [gameData.Characters, 'Charname'],
            [gameData.Objects, 'name'],
            [gameData.Rooms, 'Name'],
            [gameData.Timers, 'Name'],
            [gameData.Variables, 'varname'],
            [[gameData.Player], 'Name']
        ];

        nameMapTargets.forEach((target) => {
            const list = target[0], propName = target[1];
            (list as any).NameMap = buildNameMap(list, propName);

            // Build NameMap for Actions and CustomProperties if present
            list.forEach((item: any) => {
                if ('Actions' in item) {
                    (item.Actions as any).NameMap = buildNameMap(item.Actions, 'name');
                }
                if ('CustomProperties' in item) {
                    (item.CustomProperties as any).NameMap = buildMap(item.CustomProperties, (prop: any) => {
                        return prop.Name;
                    });
                }
            });
        });

        // Though it usually produces a UniqueID, sometimes
        // when you manually edit a field in the Rags designer
        // (instead of selecting from a dropdown) the value
        // for a room (in e.x. CT_MOVEPLAYER) will be the
        // room name or `%{name}-%{sdesc}`. So we need to check for that.
        buildMap(gameData.Rooms, (room: any) => {
            return [room.Name, room.SDesc].join('-').trim().toLowerCase();
        }, (gameData.Rooms as any).NameMap);

        // UidMap: Objects, Rooms
        (gameData.Objects as any).UidMap = buildUidMap(gameData.Objects, 'UniqueIdentifier');
        (gameData.Rooms as any).UidMap = buildUidMap(gameData.Rooms, 'UniqueID');

        const assignments = gameData.GroupAssignments || {};

        // GroupMap: Rooms, Objects, Characters, Variables, Timers, Images
        (gameData.Rooms as any).GroupMap = buildGroupMap(gameData.Rooms, 'Group');
        applyGroupAssignments(gameData.Rooms, assignments.rooms, (room) => room.UniqueID, 'Group', (gameData.Rooms as any).GroupMap);

        (gameData.Objects as any).GroupMap = buildGroupMap(gameData.Objects, 'GroupName');
        applyGroupAssignments(gameData.Objects, assignments.objects, (obj) => obj.UniqueIdentifier, 'GroupName', (gameData.Objects as any).GroupMap);

        (gameData.Characters as any).GroupMap = buildGroupMap(gameData.Characters, 'GroupName');
        applyGroupAssignments(gameData.Characters, assignments.characters, (char) => char.Charname, 'GroupName', (gameData.Characters as any).GroupMap);

        (gameData.Variables as any).GroupMap = buildGroupMap(gameData.Variables, 'GroupName');
        applyGroupAssignments(gameData.Variables, assignments.variables, (variable) => variable.varname, 'GroupName', (gameData.Variables as any).GroupMap);

        (gameData.Timers as any).GroupMap = buildGroupMap(gameData.Timers, 'GroupName');
        applyGroupAssignments(gameData.Timers, assignments.timers, (timer) => timer.Name, 'GroupName', (gameData.Timers as any).GroupMap);

        (gameData.Images as any).GroupMap = buildGroupMap(gameData.Images, 'GroupName');
        applyGroupAssignments(
            gameData.Images,
            assignments.media,
            (image) => {
                const name = image.TheName || image.curfilename || image.filename || image.name;
                if (!name) return [];
                const normalized = String(name);
                if (normalized.indexOf('/') === -1) {
                    return [normalized, `images/${normalized}`, `audio/${normalized}`];
                }
                return [normalized];
            },
            'GroupName',
            (gameData.Images as any).GroupMap
        );

        // Action owners (not a map but handy to initialize now)
        const ownerLists: any[][] = [
            TheGame.Rooms,
            TheGame.Characters,
            TheGame.Objects,
            TheGame.Timers,
            [TheGame.Player]
        ];
        for (const list of ownerLists) {
            for (const owner of list) {
                for (const act of owner.Actions) {
                    act._owner = owner;
                }
            }
        }
    }
};

// Make available globally
(window as any).Finder = Finder;
